import type { Component } from 'solid-js'
import { createMemo, createSignal, For, Show } from 'solid-js'
import { formatCountryName } from '../../../shared/helpers/country-formatter'
import { clickOutside } from '../../../shared/directives/click-outside'
import { Icon } from '@iconify-icon/solid'
import { useTransContext } from '@mbarzda/solid-i18next'

export type RegionFilterData = Record<string, string[]>

export interface RegionFilterProps {
  data: RegionFilterData
  selectedCountries: string[]
  selectedRegions: string[]
  onCountryChange: (countries: string[]) => void
  onRegionChange: (regions: string[]) => void
}

const RegionFilter: Component<RegionFilterProps> = (props) => {
  const [t] = useTransContext()
  const [isOpen, setIsOpen] = createSignal(false)
  const [searchQuery, setSearchQuery] = createSignal('')

  const countryCodes = createMemo(() => Object.keys(props.data))

  const hasSelections = () => props.selectedCountries.length > 0

  const label = () => {
    const countries = props.selectedCountries
    const regions = props.selectedRegions

    if (countries.length === 0) return t('ls_user_directory_region_country')

    const countryCount = countries.length
    const regionCount = regions.length

    if (countryCount === 1) {
      const countryName = formatCountryName(countries[0])
      if (regionCount === 0) return countryName
      if (regionCount === 1) return `${countryName} · ${regions[0]}`
      return `${countryName} · ${regionCount} ${t('ls_generic_regions')}`
    }

    if (regionCount === 0) return `${countryCount} ${t('ls_generic_countries')}`
    return `${countryCount} ${t('ls_generic_countries')} · ${regionCount} ${t('ls_generic_regions')}`
  }

  const regionsForCountry = (countryCode: string): string[] => props.data[countryCode] ?? []

  const findCountryForRegion = (region: string): string | undefined => {
    for (const code of countryCodes()) {
      if (regionsForCountry(code).includes(region)) return code
    }
    return undefined
  }

  const filteredGroups = createMemo(() => {
    const search = searchQuery().toLowerCase().trim()
    const result: { countryCode: string; countryName: string; regions: string[]; hasRegionMatch: boolean }[] = []

    for (const code of countryCodes()) {
      const countryName = formatCountryName(code)
      const allRegions = regionsForCountry(code)

      if (!search) {
        result.push({ countryCode: code, countryName, regions: allRegions, hasRegionMatch: false })
        continue
      }

      const countryMatches = countryName.toLowerCase().includes(search)
      const matchingRegions = allRegions.filter((r) => r.toLowerCase().includes(search))

      if (countryMatches) {
        result.push({ countryCode: code, countryName, regions: allRegions, hasRegionMatch: false })
      } else if (matchingRegions.length > 0) {
        result.push({ countryCode: code, countryName, regions: matchingRegions, hasRegionMatch: true })
      }
    }

    return result
  })

  const hasFilteredResults = () => filteredGroups().length > 0

  const handleCountryToggle = (countryCode: string) => {
    const isSelected = props.selectedCountries.includes(countryCode)
    const countryRegions = regionsForCountry(countryCode)

    if (isSelected) {
      // Remove country and its regions
      props.onCountryChange(props.selectedCountries.filter((c) => c !== countryCode))
      props.onRegionChange(props.selectedRegions.filter((r) => !countryRegions.includes(r)))
    } else {
      // Add country (keep existing selections)
      props.onCountryChange([...props.selectedCountries, countryCode])
    }
  }

  const handleRegionToggle = (region: string) => {
    const isSelected = props.selectedRegions.includes(region)

    if (isSelected) {
      props.onRegionChange(props.selectedRegions.filter((r) => r !== region))
    } else {
      // Auto-select parent country if not selected
      const parentCountry = findCountryForRegion(region)
      if (parentCountry && !props.selectedCountries.includes(parentCountry)) {
        props.onCountryChange([...props.selectedCountries, parentCountry])
      }
      props.onRegionChange([...props.selectedRegions, region])
    }
  }

  const clearAll = () => {
    props.onCountryChange([])
    props.onRegionChange([])
  }

  const handleSearchInput = (e: Event) => {
    const target = e.target as HTMLInputElement
    setSearchQuery(target.value)
  }

  const close = () => {
    setIsOpen(false)
    setSearchQuery('')
  }

  const toggle = () => {
    const opening = !isOpen()
    setIsOpen(opening)
    if (!opening) setSearchQuery('')
  }

  return (
    <div
      class="relative"
      ref={(el) => {
        clickOutside(el, close)
      }}
    >
      {/* Trigger button */}
      <div class="flex items-center overflow-hidden rounded-full border border-gray-200">
        <button type="button" onClick={toggle} class="flex items-center gap-2 bg-white px-3 py-2">
          <span class="whitespace-nowrap text-sm text-gray-700">{label()}</span>
          <span class={`text-gray-400 transition-transform duration-200 ${isOpen() ? 'rotate-180' : ''}`}>
            <Icon icon="heroicons:chevron-down" class="text-[1rem]" />
          </span>
        </button>
        <Show when={hasSelections()}>
          <button
            type="button"
            onClick={clearAll}
            class="flex h-9 w-9 items-center justify-center border-l border-gray-200 bg-white hover:bg-gray-50"
          >
            <Icon icon="heroicons:x-mark" class="text-[1rem]" />
          </button>
        </Show>
      </div>

      {/* Dropdown panel */}
      <div
        class={`absolute right-0 top-full z-50 mt-1 w-80 origin-top rounded-lg border border-gray-200 bg-white shadow-lg transition-all duration-200 ease-out ${
          isOpen() ? 'pointer-events-auto scale-y-100 opacity-100' : 'pointer-events-none scale-y-95 opacity-0'
        }`}
      >
        {/* Search */}
        <div class="border-b border-gray-200 px-3 py-2">
          <div class="flex items-center gap-2">
            <div class="text-gray-400">
              <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
            </div>
            <input
              type="text"
              value={searchQuery()}
              onInput={handleSearchInput}
              placeholder={t('ls_generic_search')}
              class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        {/* Grouped list */}
        <div class="max-h-72 overflow-y-auto p-2">
          <Show
            when={hasFilteredResults()}
            fallback={<div class="px-2 py-3 text-center text-sm text-gray-400">{t('ls_generic_no_results')}</div>}
          >
            <For each={filteredGroups()}>
              {(group) => {
                const isCountrySelected = () => props.selectedCountries.includes(group.countryCode)
                const hasSelectedRegions = () => group.regions.some((r) => props.selectedRegions.includes(r))
                const isExpanded = () => isCountrySelected() || hasSelectedRegions() || group.hasRegionMatch

                return (
                  <div class="mb-1 last:mb-0">
                    {/* Country row */}
                    <button
                      type="button"
                      onClick={() => handleCountryToggle(group.countryCode)}
                      class="flex w-full cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-left transition-colors duration-150"
                      classList={{
                        'bg-primary-50 text-primary-700': isCountrySelected(),
                        'text-gray-700 hover:bg-gray-50': !isCountrySelected()
                      }}
                    >
                      <div class="flex items-center gap-2">
                        <div
                          class="flex h-4 w-4 items-center justify-center rounded border transition-colors"
                          classList={{
                            'border-primary-500 bg-primary-500 text-white': isCountrySelected(),
                            'border-gray-300 bg-white': !isCountrySelected()
                          }}
                        >
                          <Show when={isCountrySelected()}>
                            <Icon icon="heroicons:check" class="text-[0.75rem]" />
                          </Show>
                        </div>
                        <span class="text-sm font-medium" classList={{ 'font-semibold': isCountrySelected() }}>
                          {group.countryName}
                        </span>
                      </div>
                      <span
                        class="transition-transform duration-200"
                        classList={{
                          'rotate-180 text-primary-500': isExpanded(),
                          'text-gray-400': !isExpanded()
                        }}
                      >
                        <Icon icon="heroicons:chevron-down" class="text-[1rem]" />
                      </span>
                    </button>

                    {/* Region items — grid controls space, transform controls visual slide */}
                    <div
                      class="grid transition-[grid-template-rows] duration-300 ease-out"
                      style={{ 'grid-template-rows': isExpanded() ? '1fr' : '0fr' }}
                    >
                      <div class="overflow-hidden">
                        <div
                          class="ml-1 mt-1 space-y-0.5 rounded-r bg-primary-50/40 py-1 pl-1 transition-[transform,opacity] duration-300 ease-out"
                          classList={{
                            'translate-y-0 opacity-100': isExpanded(),
                            '-translate-y-2 opacity-0': !isExpanded()
                          }}
                        >
                          <Show
                            when={group.regions.length > 0}
                            fallback={
                              <p class="px-2 py-1.5 text-sm italic text-gray-400">
                                {t('ls_user_directory_no_regions')}
                              </p>
                            }
                          >
                            <For each={group.regions}>
                              {(region) => {
                                const isSelected = () => props.selectedRegions.includes(region)
                                return (
                                  <button
                                    type="button"
                                    onClick={() => handleRegionToggle(region)}
                                    class="flex w-full cursor-pointer items-center rounded px-2 py-1.5 text-left text-sm transition-colors duration-150"
                                    classList={{
                                      'bg-primary-50 text-primary-700 font-medium': isSelected(),
                                      'text-gray-700 hover:bg-gray-50': !isSelected()
                                    }}
                                  >
                                    <div class="flex items-center gap-2">
                                      <div
                                        class="flex h-4 w-4 items-center justify-center rounded border transition-colors"
                                        classList={{
                                          'border-primary-500 bg-primary-500 text-white': isSelected(),
                                          'border-gray-300 bg-white': !isSelected()
                                        }}
                                      >
                                        <Show when={isSelected()}>
                                          <Icon icon="heroicons:check" class="text-[0.75rem]" />
                                        </Show>
                                      </div>
                                      <span>{region}</span>
                                    </div>
                                  </button>
                                )
                              }}
                            </For>
                          </Show>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }}
            </For>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default RegionFilter
